import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const SEVERITY_CONFIG = {
  critical: {
    bg: 'bg-destructive/10',
    border: 'border-destructive/30',
    text: 'text-destructive',
    badge: 'bg-destructive/20 text-destructive',
    icon: 'AlertOctagon',
  },
  warning: {
    bg: 'bg-warning/10',
    border: 'border-warning/30',
    text: 'text-warning',
    badge: 'bg-warning/20 text-warning',
    icon: 'AlertTriangle',
  },
  info: {
    bg: 'bg-accent/10',
    border: 'border-accent/30',
    text: 'text-accent',
    badge: 'bg-accent/20 text-accent',
    icon: 'Info',
  },
};

const TYPE_LABELS = {
  expired_document: 'Expired Doc',
  expiring_critical: 'Expiring <=7d',
  expiring_soon: 'Expiring Soon',
  missing_expiry: 'No Expiry Date',
  missing_documents: 'Missing Docs',
  stale_snag: 'Stale Snag',
  overdue_inspection: 'No Inspection',
};

const SCORE_COLOR = (score) => {
  if (score >= 85) return { bar: 'bg-success', text: 'text-success' };
  if (score >= 70) return { bar: 'bg-accent', text: 'text-accent' };
  if (score >= 50) return { bar: 'bg-warning', text: 'text-warning' };
  return { bar: 'bg-destructive', text: 'text-destructive' };
};

const AlertItem = ({ alert }) => {
  const navigate = useNavigate();
  const cfg = SEVERITY_CONFIG?.[alert?.severity] || SEVERITY_CONFIG?.info;
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border ${cfg?.bg} ${cfg?.border} cursor-pointer hover:opacity-90 transition-opacity`}
      onClick={() => navigate(alert?.link)}
    >
      <div className={`mt-0.5 flex-shrink-0 ${cfg?.text}`}>
        <Icon name={cfg?.icon} size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${cfg?.badge}`}>
            {TYPE_LABELS?.[alert?.type] || alert?.type}
          </span>
          <span className="text-xs text-muted-foreground truncate">{alert?.siteName}</span>
        </div>
        <p className="text-xs text-foreground leading-snug">{alert?.description}</p>
      </div>
      <Icon name="ChevronRight" size={14} className="text-muted-foreground flex-shrink-0 mt-1" />
    </div>
  );
};

const ComplianceWatch = ({ alerts = [], siteScores = [], loading = false, complianceData = {}, filteredSiteIds = null }) => {
  const [showAll, setShowAll] = useState(false);

  const filteredAlerts = filteredSiteIds
    ? alerts?.filter(a => filteredSiteIds?.includes(a?.siteId))
    : alerts;
  const filteredScores = filteredSiteIds
    ? siteScores?.filter(s => filteredSiteIds?.includes(s?.siteId))
    : siteScores;

  const criticalCount = filteredAlerts?.filter(a => a?.severity === 'critical')?.length;
  const warningCount = filteredAlerts?.filter(a => a?.severity === 'warning')?.length;
  const displayedAlerts = showAll ? filteredAlerts : filteredAlerts?.slice(0, 6);

  const avgScore = filteredScores?.length > 0
    ? Math.round(filteredScores?.reduce((sum, s) => sum + s?.score, 0) / filteredScores?.length)
    : null;

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="h-5 bg-muted rounded w-40 mb-4 animate-pulse" />
        <div className="space-y-3">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-destructive/10 rounded-lg">
            <Icon name="ShieldAlert" size={18} className="text-destructive" />
          </div>
          <div>
            <h2 className="text-base md:text-lg font-semibold text-foreground font-heading">
              Compliance Watch
            </h2>
            <p className="text-xs text-muted-foreground">
              {filteredSiteIds
                ? `${filteredScores?.length} site${filteredScores?.length !== 1 ? 's' : ''} monitored`
                : 'Live risk flags across all sites'}
            </p>
          </div>
        </div>
        {avgScore !== null && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Avg Score</p>
            <p className={`text-xl font-bold ${SCORE_COLOR(avgScore)?.text}`}>{avgScore}</p>
          </div>
        )}
      </div>

      {filteredAlerts?.length > 0 && (
        <div className="flex gap-2 mb-4">
          {criticalCount > 0 && (
            <span className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-destructive/15 text-destructive">
              <Icon name="AlertOctagon" size={12} />
              {criticalCount} Critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-warning/15 text-warning">
              <Icon name="AlertTriangle" size={12} />
              {warningCount} Warning
            </span>
          )}
        </div>
      )}

      <div>
        {filteredAlerts?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="p-3 bg-success/10 rounded-full mb-3">
              <Icon name="ShieldCheck" size={24} className="text-success" />
            </div>
            <p className="text-sm font-medium text-foreground">All clear</p>
            <p className="text-xs text-muted-foreground mt-1">No compliance issues detected across your sites.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayedAlerts?.map(alert => (
              <AlertItem key={alert?.id} alert={alert} />
            ))}
            {filteredAlerts?.length > 6 && (
              <button
                onClick={() => setShowAll(prev => !prev)}
                className="w-full text-xs text-accent hover:text-accent/80 py-2 transition-colors"
              >
                {showAll ? 'Show less' : `Show ${filteredAlerts?.length - 6} more alerts`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceWatch;
